// FeedChart.tsx — one curve metric, every selected feed drawn on it.
//
// Fetches the downsampled curve for each feed from /curves and renders a
// recharts LineChart plus the summary table beneath it: tail mean and best
// value, each diffed against the first feed (the baseline). Raw points stay
// visible behind the smoothed line when smoothing is on, so the EMA never
// hides the noise it is averaging.
//
// This is the chart the deleted live-metrics page carried, unchanged except
// for taking its feeds from the commit selection instead of a run picker.
//
// A dirty curve (/curves says a dirty mark covers it) is drawn faded and
// dashed, its summary row struck through with the mark's reason on hover,
// and it is never the baseline the other feeds are diffed against: the
// baseline is the first clean feed, and with none there are no deltas.
import { useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';
import { unwrapJson } from 'common';
import { apiClient } from '../api';
import type { CurvesResponse, RunCurve } from '../api';
import { seriesColor } from './colors';
import { DIRTY_OPACITY, DIRTY_STYLE, dirtyLabel, dirtyTitle } from './dirty';
import { feedLabel, nearest, smoothValues, type FeedRef } from './feeds';
import { formatChartNum } from './format';
import type { ThemeMode } from './theme';

interface Series {
  feed: FeedRef;
  curve: RunCurve;
  smoothed: number[];
}

export default function FeedChart({
  owner, name, metric, feeds, mode, smooth, logY, tick,
}: {
  owner: string;
  name: string;
  metric: string;
  feeds: FeedRef[];
  mode: ThemeMode;
  smooth: number;
  logY: boolean;
  tick: number;
}) {
  const [data, setData] = useState<CurvesResponse | null>(null);
  const [error, setError] = useState('');
  const ids = feeds.map((f) => f.run.id);
  const idsKey = ids.join(',');

  useEffect(() => {
    let cancelled = false;
    setError('');
    // Both spellings of the run list: the comma form the route has always
    // taken, and the repeated form the rework's contract writes. Sending
    // both keeps the pane working against either server, and costs nothing
    // against one that reads both — the handler de-duplicates run ids.
    const params = new URLSearchParams({ metric, runs: idsKey, max_points: '1000' });
    for (const id of ids) params.append('run', String(id));
    (async () => {
      try {
        const res = await apiClient.fetch(
          `/api/v1/repos/${owner}/${name}/curves?${params.toString()}`,
        );
        const body = await unwrapJson<CurvesResponse>(res);
        if (!cancelled) setData(body);
      } catch (err) {
        if (!cancelled) {
          setData(null);
          setError(err instanceof Error ? err.message : 'Failed to load curves');
        }
      }
    })();
    return () => { cancelled = true; };
    // ids is rebuilt every render; idsKey is its identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [owner, name, metric, idsKey, tick]);

  const series = useMemo<Series[]>(() => {
    if (!data) return [];
    const byId = new Map(feeds.map((f) => [f.run.id, f]));
    return (data.curves ?? [])
      .filter((c) => byId.has(c.run_id) && c.points.length > 0)
      .map((c) => ({
        feed: byId.get(c.run_id)!,
        curve: c,
        smoothed: smoothValues(c.points, smooth),
      }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, idsKey, smooth]);

  // One row per distinct step across feeds; feeds without that exact step
  // leave a gap that connectNulls bridges.
  const rows = useMemo(() => {
    const byStep = new Map<number, Record<string, number>>();
    for (const s of series) {
      s.curve.points.forEach(([step, v], i) => {
        let row = byStep.get(step);
        if (!row) {
          row = { step };
          byStep.set(step, row);
        }
        row[`raw${s.feed.run.id}`] = v;
        row[`s${s.feed.run.id}`] = s.smoothed[i];
      });
    }
    return Array.from(byStep.values()).sort((a, b) => a.step - b.step);
  }, [series]);

  const canLog = logY && series.every((s) => s.curve.summary && s.curve.summary.min > 0);
  const higher = data?.metric.direction === 'higher_is_better';
  const baseline = series.find((s) => !s.curve.dirty);

  return (
    <section>
      <h3 className="h6 mb-2">
        <code>{metric}</code>
        {data?.metric.unit && <span className="text-secondary"> ({data.metric.unit})</span>}
        <span className="text-secondary ms-2">
          {higher ? 'higher is better' : 'lower is better'}
          {logY && !canLog && ' · log scale needs positive values'}
        </span>
      </h3>
      {error && <div className="alert alert-danger">{error}</div>}
      <div className="chart-wrap" style={{ height: 300 }}>
        {series.length === 0 ? (
          <div className="text-secondary">
            {data ? 'None of the selected feeds have this curve.' : 'Loading…'}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={rows} margin={{ top: 8, right: 16, bottom: 4, left: 4 }}>
              <CartesianGrid stroke="var(--bs-border-color)" strokeOpacity={0.5} vertical={false} />
              <XAxis
                dataKey="step"
                type="number"
                domain={['dataMin', 'dataMax']}
                tickFormatter={(v: number) => formatChartNum(v)}
                stroke="var(--bs-border-color)"
                tick={{ fill: 'var(--bs-secondary-color)', fontSize: 11 }}
              />
              <YAxis
                scale={canLog ? 'log' : 'auto'}
                domain={['auto', 'auto']}
                allowDataOverflow={canLog}
                tickFormatter={(v: number) => formatChartNum(v)}
                width={64}
                stroke="var(--bs-border-color)"
                tick={{ fill: 'var(--bs-secondary-color)', fontSize: 11 }}
              />
              <Tooltip
                isAnimationActive={false}
                cursor={{ stroke: 'var(--bs-secondary-color)', strokeDasharray: '3 3' }}
                content={({ active, label }) => {
                  if (!active || typeof label !== 'number') return null;
                  return (
                    <div className="feeds-tooltip">
                      <div className="text-secondary">step {formatChartNum(label)}</div>
                      {series.map((s) => {
                        const i = nearest(s.curve.points, label);
                        if (i === undefined) return null;
                        return (
                          <div key={s.feed.run.id} className="feeds-chip">
                            <span
                              className="feeds-swatch"
                              style={{ background: seriesColor(s.feed.slot, mode) }}
                            />
                            <span>{feedLabel(s.feed)}</span>
                            <strong
                              className="ms-auto"
                              style={s.curve.dirty ? DIRTY_STYLE : undefined}
                              aria-label={s.curve.dirty
                                ? `${formatChartNum(s.smoothed[i])}, ${dirtyLabel(s.curve.dirty_marks)}`
                                : undefined}
                            >
                              {formatChartNum(s.smoothed[i])}
                            </strong>
                          </div>
                        );
                      })}
                    </div>
                  );
                }}
              />
              {smooth > 0 && series.map((s) => (
                <Line
                  key={`raw${s.feed.run.id}`}
                  dataKey={`raw${s.feed.run.id}`}
                  stroke={seriesColor(s.feed.slot, mode)}
                  strokeOpacity={0.2}
                  strokeWidth={1}
                  dot={false}
                  activeDot={false}
                  connectNulls
                  isAnimationActive={false}
                />
              ))}
              {series.map((s) => (
                <Line
                  key={`s${s.feed.run.id}`}
                  dataKey={`s${s.feed.run.id}`}
                  stroke={seriesColor(s.feed.slot, mode)}
                  strokeOpacity={s.curve.dirty ? DIRTY_OPACITY : 1}
                  strokeDasharray={s.curve.dirty ? '6 4' : undefined}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                  connectNulls
                  isAnimationActive={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
      {series.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table className="table table-sm feeds-summary">
            <thead>
              <tr>
                <th>Feed</th>
                <th title="Mean of the last 5% of points">Final</th>
                <th>Δ final</th>
                <th>Best</th>
                <th>Δ best</th>
                <th>Steps</th>
              </tr>
            </thead>
            <tbody>
              {series.map((s) => {
                const isBase = s === baseline;
                const sum = s.curve.summary;
                const base = baseline?.curve.summary;
                if (!sum) return null;
                const best = higher ? sum.max : sum.min;
                const bestStep = higher ? sum.max_step : sum.min_step;
                const baseBest = base ? (higher ? base.max : base.min) : undefined;
                const delta = (value: number, ref: number | undefined) => (
                  s.curve.dirty || ref === undefined
                    ? <span className="text-secondary">—</span>
                    : <Delta value={value} base={ref} higher={higher} self={isBase} />
                );
                return (
                  <tr
                    key={s.feed.run.id}
                    className={s.curve.dirty ? 'dirty-row' : undefined}
                    title={dirtyTitle(s.curve.dirty_marks) || undefined}
                    aria-label={s.curve.dirty ? `${feedLabel(s.feed)}, ${dirtyLabel(s.curve.dirty_marks)}` : undefined}
                  >
                    <td>
                      <span className="feeds-chip">
                        <span
                          className="feeds-swatch"
                          style={{ background: seriesColor(s.feed.slot, mode) }}
                        />
                        {feedLabel(s.feed)}
                        {isBase && series.length > 1 && (
                          <span className="text-secondary">(baseline)</span>
                        )}
                        {s.curve.dirty && <span className="text-secondary">(dirty)</span>}
                      </span>
                    </td>
                    <td>{formatChartNum(sum.tail_mean)}</td>
                    <td>
                      {delta(sum.tail_mean, base?.tail_mean)}
                    </td>
                    <td>
                      {formatChartNum(best)}{' '}
                      <span className="text-secondary">@ {formatChartNum(bestStep)}</span>
                    </td>
                    <td>{delta(best, baseBest)}</td>
                    <td className="text-secondary">
                      {formatChartNum(sum.last_step)} ({sum.points} pts)
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// Signed change vs the baseline, worded as better/worse per the metric's
// direction so the color is never the only cue.
function Delta({ value, base, higher, self }: {
  value: number; base: number; higher: boolean; self: boolean;
}) {
  if (self) return <span className="text-secondary">—</span>;
  const d = value - base;
  if (d === 0) return <span className="text-secondary">±0</span>;
  const better = higher ? d > 0 : d < 0;
  const pct = base !== 0 ? ` (${d > 0 ? '+' : ''}${((d / Math.abs(base)) * 100).toFixed(1)}%)` : '';
  return (
    <span className={better ? 'text-success' : 'text-danger'}>
      {better ? '▲ better' : '▼ worse'} {d > 0 ? '+' : ''}{formatChartNum(d)}{pct}
    </span>
  );
}
